// src/components/Navbar.tsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// ── Admin menu items ─────────────────────────────────────────────
const ADMIN_ITEMS = [
  { label: 'Experiences',  path: '/admin/experience'  },
  { label: 'Skills',       path: '/admin/skills'      },
  { label: 'Education',    path: '/admin/education'   },
  { label: 'Publications', path: '/admin/publications'},
  { label: 'Footer',       path: '/admin/footer'      },
];

// ── Public nav items ─────────────────────────────────────────────
const PUBLIC_ITEMS = [
  { label: 'Experience', path: '/experience' },
  { label: 'Education',  path: '/education'  },
];

const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Desktop admin dropdown
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const adminMenuOpen = Boolean(anchorEl);

  // Mobile drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileAdminOpen, setMobileAdminOpen] = useState(false);

  const isAdmin = isAuthenticated && (user?.role === 'admin' || user?.role === 'editor');

  // ── Desktop handlers ─────────────────────────────────────────
  const handleAdminMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleAdminMenuClose = () => setAnchorEl(null);
  const handleDesktopNavigate = (path: string) => {
    navigate(path);
    handleAdminMenuClose();
  };

  // ── Mobile handlers ──────────────────────────────────────────
  const toggleDrawer = () => setDrawerOpen(prev => !prev);
  const closeDrawer  = () => { setDrawerOpen(false); setMobileAdminOpen(false); };
  const handleMobileNavigate = (path: string) => {
    navigate(path);
    closeDrawer();
  };

  // ── Mobile drawer content ────────────────────────────────────
  const drawerContent = (
    <Box sx={{ width: 260 }} role="presentation">
      <List>
        {PUBLIC_ITEMS.map(item => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton onClick={() => handleMobileNavigate(item.path)}>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}

        {isAdmin && (
          <>
            <Divider />
            <ListItem disablePadding>
              <ListItemButton onClick={() => setMobileAdminOpen(prev => !prev)}>
                <ListItemText primary="Manage" />
                {mobileAdminOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={mobileAdminOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {ADMIN_ITEMS.map(item => (
                  <ListItemButton
                    key={item.path}
                    sx={{ pl: 4 }}
                    onClick={() => handleMobileNavigate(item.path)}
                  >
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}

        <Divider />
        {isAuthenticated ? (
          <ListItem disablePadding>
            <ListItemButton onClick={() => { logout(); closeDrawer(); }}>
              <ListItemText primary={`Logout (${user?.username})`} />
            </ListItemButton>
          </ListItem>
        ) : (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileNavigate('/login')}>
              <ListItemText primary="Login" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar position="static">
      <Toolbar>
        {/* Brand */}
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          <Link to="/" style={{ color: 'white', textDecoration: 'none' }}>
            Portfolio
          </Link>
        </Typography>

        {isMobile ? (
          /* ── Mobile: hamburger ─────────────────────────────── */
          <>
            <IconButton
              color="inherit"
              aria-label="open navigation menu"
              edge="end"
              onClick={toggleDrawer}
            >
              <MenuIcon />
            </IconButton>
            <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
              {drawerContent}
            </Drawer>
          </>
        ) : (
          /* ── Desktop: inline buttons ───────────────────────── */
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {PUBLIC_ITEMS.map(item => (
              <Button key={item.path} color="inherit" component={Link} to={item.path}>
                {item.label}
              </Button>
            ))}

            {isAdmin && (
              <>
                <Button
                  color="inherit"
                  onClick={handleAdminMenuClick}
                  endIcon={<ExpandMoreIcon />}
                >
                  Manage
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={adminMenuOpen}
                  onClose={handleAdminMenuClose}
                >
                  {ADMIN_ITEMS.map(item => (
                    <MenuItem
                      key={item.path}
                      onClick={() => handleDesktopNavigate(item.path)}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {isAuthenticated ? (
              <Button color="inherit" onClick={logout}>
                Logout ({user?.username})
              </Button>
            ) : (
              <Button color="inherit" component={Link} to="/login">
                Login
              </Button>
            )}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
